import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { inviteAPI, authAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Loader2, ArrowRight, Calendar, Clock, Users, Lock, Check } from "lucide-react";

export default function InviteAcceptPage() {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);

  useEffect(() => {
    const acceptInvite = async () => {
      try {
        const res = await inviteAPI.accept(inviteToken);
        setInviteData(res.data);
        localStorage.setItem("usg_token", res.data.token);
        localStorage.setItem("usg_user", JSON.stringify(res.data.user));
        setAccepted(true);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired invite link");
      } finally {
        setLoading(false);
      }
    };
    acceptInvite();
  }, [inviteToken]);

  const handleSetPassword = async () => {
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setSettingPassword(true);
    try {
      await authAPI.setPassword(password);
      setPasswordSet(true);
      toast.success("Password set! You can now sign in with email & password.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set password");
    } finally {
      setSettingPassword(false);
    }
  };

  const goToBoard = () => {
    if (inviteData?.board_id) {
      window.location.href = `/board/${inviteData.board_id}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="invite-accept-page">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto" />
          <p className="text-slate-500 font-medium">Accepting invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="invite-accept-page">
        <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md text-center shadow-sm">
          <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl font-bold">!</span>
          </div>
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Invite Invalid</h2>
          <p className="text-slate-500 text-sm mb-6">{error}</p>
          <Button onClick={() => navigate("/login")} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="invite-accept-page">
      <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md shadow-sm animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-heading font-bold text-lg">U</span>
          </div>
          <span className="font-heading font-bold text-lg text-slate-900">USG</span>
        </div>

        <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
          <Users className="w-7 h-7 text-emerald-600" />
        </div>

        <h2 className="font-heading font-bold text-2xl text-slate-900 mb-2">You're In!</h2>
        <p className="text-slate-500 mb-6">
          You've been invited to <strong className="text-slate-700">{inviteData?.board_name}</strong>.
        </p>

        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-2 mb-6">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="capitalize">{inviteData?.game_mode || "Async"} Session</span>
          </div>
          {inviteData?.scheduled_start && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock className="w-4 h-4 text-slate-400" />
              <span>Starts: {new Date(inviteData.scheduled_start).toLocaleString()}</span>
            </div>
          )}
        </div>

        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs mb-4">
          Logged in as {inviteData?.user?.email}
        </Badge>

        {/* Password Set Section */}
        {!passwordSet ? (
          <div className="border-t border-slate-200 pt-5 mt-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Set your password</span>
            </div>
            <p className="text-xs text-slate-400">Create a password so you can sign in directly next time.</p>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Password</Label>
                <Input
                  type="password"
                  data-testid="invite-set-password"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-10 bg-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Confirm Password</Label>
                <Input
                  type="password"
                  data-testid="invite-confirm-password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-10 bg-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="set-password-btn"
                  onClick={handleSetPassword}
                  disabled={settingPassword || !password}
                  size="sm"
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                  {settingPassword ? "Setting..." : "Set Password"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="skip-password-btn"
                  onClick={goToBoard}
                  className="text-slate-400 text-xs"
                >
                  Skip for now
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="border-t border-slate-200 pt-5 mt-5">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 mb-4">
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700 font-medium">Password set successfully!</span>
            </div>
          </div>
        )}

        <Button
          data-testid="go-to-board-btn"
          onClick={goToBoard}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11 font-medium mt-4 active:scale-[0.98]"
        >
          Open Game Board
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
